import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import WrapperComponent from '../commonComponents/WrapperComponent';
import ContactTypeSelector from './ContactTypeSelector';
import FileUpload from './FileUpload';
import GroupOptions from './GroupOptions';
import { url } from '../../util/config';

const CreateContactGroupComponent = () => {
    const [contactType, setContactType] = useState('Number');
    const [uploadFile, setUploadFile] = useState(null);
    const [groupOption, setGroupOption] = useState('CreateNew');
    const [groupName, setGroupName] = useState('');
    const [existingGroups, setExistingGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [errorMessageInput, setErrorInputMessage] = useState('');
    const [segmentList, setSegmentList] = useState([]);
    const [selectedSegmentId, setSelectedSegmentId] = useState('');
    const [segmentJsonHeaders, setSegmentJsonHeaders] = useState([]);
    const [fileHeaderError, setFileHeaderError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (contactType === 'Segment') {
            const fetchSegments = async () => {
                const token = localStorage.getItem('token');
                try {
                    const response = await fetch(url + '/segmentation', {
                        headers: { Authorization: 'Bearer ' + token },
                    });
                    if (!response.ok) throw new Error('Failed to fetch segments');
                    const data = await response.json();
                    setSegmentList(data);
                } catch (error) {
                    setSegmentList([]);
                }
            };
            fetchSegments();
        }
    }, [contactType]);

    useEffect(() => {
        if (contactType === 'Segment' && selectedSegmentId) {
            const selectedSegment = segmentList.find(s => s._id === selectedSegmentId);
            if (selectedSegment && selectedSegment.segment_json) {
                const extractHeaders = (rules) => {
                    const headersSet = new Set();
                    const traverse = (rules) => {
                        for (const rule of rules) {
                            if (rule.field) headersSet.add(rule.field);
                            if (rule.children && rule.children.length > 0) traverse(rule.children);
                        }
                    };
                    traverse(rules);
                    return Array.from(headersSet);
                };
                setSegmentJsonHeaders(extractHeaders(selectedSegment.segment_json || []));
            } else {
                setSegmentJsonHeaders([]);
            }
        }
    }, [selectedSegmentId, segmentList, contactType]);

    const validateFileHeaders = (file) => {
        setFileHeaderError('');
        if (contactType === 'Segment' && selectedSegmentId && file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                const text = e.target.result;
                const firstLine = text.split('\n')[0].trim();
                const fileHeaders = firstLine.split(',').map(h => h.trim());
                const missingHeaders = segmentJsonHeaders.filter(h => !fileHeaders.includes(h));
                if (missingHeaders.length > 0) {
                    setFileHeaderError(`File is missing required headers: ${missingHeaders.join(', ')}`);
                }
            };
            reader.readAsText(file);
        }
    };

    useEffect(() => {
        if (uploadFile) validateFileHeaders(uploadFile);
        // eslint-disable-next-line
    }, [uploadFile, segmentJsonHeaders]);

    const fetchExistingGroups = async () => {
        const token = localStorage.getItem('token');
        setErrorInputMessage('');
        try {
            const response = await fetch(`${url}/contacts/groupnames`, {
                method: 'GET',
                headers: { Authorization: 'Bearer ' + token },
            });
            if (!response.ok) throw new Error('Failed to fetch groups');
            const data = await response.json();
            setExistingGroups(data);
        } catch (error) {
            console.error('Error fetching groups:', error);
        }
    };

    const handleReset = () => {
        setContactType('Number');
        setUploadFile(null);
        setGroupOption('CreateNew');
        setGroupName('');
        setSelectedGroup('');
        setErrorMessage('');
        setErrorInputMessage('');
        setFileHeaderError('');
        setSelectedSegmentId('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (groupOption === 'CreateNew' && (groupName.length < 4 || groupName.length > 20)) {
            setErrorInputMessage('Group name must be between 4 and 20 characters.');
            return;
        }

        if (contactType === 'Segment') {
            if (!selectedSegmentId) { toast.error('Please select a segment.', { autoClose: 5000 }); return; }
            if (fileHeaderError) { toast.error(fileHeaderError, { autoClose: 5000 }); return; }
        }

        const formData = new FormData();
        formData.append('contact_type', contactType);
        formData.append('groupOption', groupOption);
        formData.append('group_name', groupOption === 'CreateNew' ? groupName : selectedGroup);
        formData.append('groupFlag', groupOption === 'CreateNew');
        if (uploadFile) formData.append('file', uploadFile);
        if (contactType === 'Segment') {
            formData.append('segmentid', selectedSegmentId);
            formData.append('segment_json_headers', JSON.stringify(segmentJsonHeaders));
        }

        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${url}/contacts`, {
                method: 'POST',
                headers: { Authorization: 'Bearer ' + token },
                body: formData,
            });

            if (response.ok) {
                const successData = await response.json();
                toast.success(successData.message || 'Group created successfully!', { autoClose: 5000 });
                handleReset();
                setTimeout(() => navigate('/manageContacts'), 5000);
            } else {
                const errorData = await response.json();
                toast.error(errorData.error || 'Failed to create group. Please try again.', { autoClose: 5000 });
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            toast.error('An error occurred. Please try again.', { autoClose: 5000 });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <WrapperComponent>
            <div style={styles.page}>
                <div style={styles.card}>
                    <div style={styles.cardHeader}>
                        <div style={styles.iconCircle}>
                            <i className="bi bi-people" style={{ fontSize: '20px', color: '#1565c0' }}></i>
                        </div>
                        <div>
                            <h5 style={styles.cardTitle}>Create Contact Group</h5>
                            <p style={styles.cardSubtitle}>Upload and organise your contacts into a group</p>
                        </div>
                    </div>

                    <div style={styles.cardBody}>
                        <form onSubmit={handleSubmit} noValidate>
                            <ContactTypeSelector contactType={contactType} setContactType={setContactType} />

                            {contactType === 'Segment' && (
                                <div style={styles.fieldGroup}>
                                    <label style={styles.label}>Segment</label>
                                    <select
                                        value={selectedSegmentId}
                                        style={styles.input}
                                        onChange={(e) => setSelectedSegmentId(e.target.value)}
                                        required
                                    >
                                        <option value="">Select Segment</option>
                                        {segmentList.map(seg => (
                                            <option key={seg._id} value={seg._id}>{seg.name}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <FileUpload
                                uploadFile={uploadFile}
                                setUploadFile={setUploadFile}
                                errorMessage={errorMessage}
                                setErrorMessage={setErrorMessage}
                            />

                            {fileHeaderError && (
                                <div style={styles.fileHeaderError}>
                                    <i className="bi bi-exclamation-triangle" style={{ marginRight: 6 }}></i>
                                    {fileHeaderError}
                                </div>
                            )}

                            <GroupOptions
                                groupOption={groupOption}
                                setGroupOption={setGroupOption}
                                groupName={groupName}
                                setGroupName={setGroupName}
                                existingGroups={existingGroups}
                                setSelectedGroup={setSelectedGroup}
                                selectedGroup={selectedGroup}
                                fetchExistingGroups={fetchExistingGroups}
                                errorMessageInput={errorMessageInput}
                            />

                            <div style={styles.actions}>
                                <button type="button" style={styles.btnSecondary} onClick={handleReset}>
                                    Reset
                                </button>
                                <button type="submit" style={styles.btnPrimary} disabled={isLoading}>
                                    {isLoading
                                        ? <><span style={styles.spinner}></span> Uploading…</>
                                        : 'Create Group'}
                                </button>
                            </div>
                        </form>
                        <ToastContainer />
                    </div>
                </div>
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
};

const styles = {
    page: { display: 'flex', justifyContent: 'center', paddingTop: '8px', fontFamily: "'Segoe UI', system-ui, sans-serif" },
    card: { background: 'white', borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)', width: '100%', maxWidth: '580px', border: '1px solid #e5e7eb', overflow: 'hidden' },
    cardHeader: { display: 'flex', alignItems: 'center', gap: '14px', padding: '20px 24px', borderBottom: '1px solid #f3f4f6', background: '#f8fafc' },
    iconCircle: { width: '44px', height: '44px', borderRadius: '10px', background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    cardTitle: { margin: 0, fontSize: '16px', fontWeight: '700', color: '#111827' },
    cardSubtitle: { margin: 0, fontSize: '13px', color: '#6b7280' },
    cardBody: { padding: '24px' },
    fieldGroup: { marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '6px' },
    label: { fontSize: '13px', fontWeight: '600', color: '#374151' },
    input: { padding: '10px 12px', fontSize: '14px', border: '1.5px solid #d1d5db', borderRadius: '8px', outline: 'none', width: '100%', boxSizing: 'border-box', color: '#111827', background: 'white' },
    fileHeaderError: { fontSize: '12px', color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '8px 12px', marginBottom: '16px', display: 'flex', alignItems: 'center' },
    actions: { display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '8px' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '10px 20px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    btnSecondary: { padding: '10px 20px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
    spinner: { width: '15px', height: '15px', border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' },
};

export default CreateContactGroupComponent;
